import { useState, useEffect, useCallback, useRef } from 'react'
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Grid,
  LinearProgress,
  TextField,
  Tabs,
  Tab,
} from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import DownloadIcon from '@mui/icons-material/Download'
import InfoIcon from '@mui/icons-material/Info'
import HistoryIcon from '@mui/icons-material/History'
import ErrorIcon from '@mui/icons-material/Error'
import { productService } from '../services'
import { formatDate } from '../utils/dateFormat'
import { getUploadSuccessMessage } from '../utils/uploadSuccessMessages'
import UploadErrorDetailsDialog from '../components/UploadErrorDetailsDialog'
import UploadLogStatusChip from '../components/UploadLogStatusChip'
import UploadHistoryRefreshButton from '../components/UploadHistoryRefreshButton'
import UploadHistoryTableWrapper from '../components/UploadHistoryTableWrapper'
import UploadImportStatusAlert from '../components/UploadImportStatusAlert'
import InstructionCard from '../components/common/InstructionCard'
import { productUploadInstructions } from '../constants/uploadInstructions'
import { useUploadHistoryPolling } from '../hooks/useUploadHistoryPolling'

const ProductUploadPage = () => {
  const [file, setFile] = useState(null)
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState(null)
  const [uploadedProducts, setUploadedProducts] = useState([])
  const [uploadProgress, setUploadProgress] = useState(0)
  const [totalItems, setTotalItems] = useState(0)
  const [processedItems, setProcessedItems] = useState(0)
  const [batchSize, setBatchSize] = useState(10)
  const [uploadLogs, setUploadLogs] = useState([])
  const [logsLoading, setLogsLoading] = useState(false)
  const [activeTab, setActiveTab] = useState(0)
  const [errorDetailsDialog, setErrorDetailsDialog] = useState({ open: false, uploadLog: null })
  const [logsPage, setLogsPage] = useState(0)
  const [logsRowsPerPage, setLogsRowsPerPage] = useState(10)
  const [logsTotalCount, setLogsTotalCount] = useState(0)
  const [isDragOver, setIsDragOver] = useState(false)
  const logsFetchInFlightRef = useRef(false)

  const handleFileChange = (e) => {
    setFile(e.target.files[0])
  }

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDragEnter = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
    
    const files = e.dataTransfer.files
    if (files && files.length > 0) {
      const droppedFile = files[0]
      if (droppedFile.type === 'text/csv' || droppedFile.name.endsWith('.csv')) {
        setFile(droppedFile)
      } else {
        setMessage({ type: 'error', text: 'Please upload a CSV file only' })
      }
    }
  }

  const handleUpload = async () => {
    if (!file) {
      setMessage({ type: 'error', text: 'Please select a file to upload' })
      return
    }

    try {
      setLoading(true)
      setUploadProgress(0)
      setProcessedItems(0)
      
      // Read file and parse CSV to count items
      const text = await file.text()
      const lines = text.split('\n').filter(line => line.trim())
      const itemCount = Math.max(0, lines.length - 1) // Subtract header
      setTotalItems(itemCount)
      
      // Simulate progress updates while uploading
      const progressInterval = setInterval(() => {
        setProcessedItems(prev => {
          const next = Math.min(prev + Math.floor(itemCount * 0.1), itemCount - 1)
          setUploadProgress(Math.round((next / itemCount) * 80)) // 80% before completion
          return next
        })
      }, 300)
      
      const result = await productService.importProducts({
        file,
        batchSize: batchSize.toString(),
      })
      
      clearInterval(progressInterval)
      setProcessedItems(itemCount)
      setUploadProgress(100)
      
      // API now returns immediately - processing happens in background
      if (result.success && result.message) {
        setMessage({
          type: 'success',
          text: result.message || getUploadSuccessMessage('product'),
          showHistoryLink: true,
        })
        setUploadedProducts([]) // No immediate products since processing is async
        setFile(null)
        
        // Refresh upload logs to show the new upload
        fetchUploadLogs()
        
        // Reset progress after 2 seconds
        setTimeout(() => {
          setUploadProgress(0)
          setProcessedItems(0)
          setTotalItems(0)
        }, 2000)
      } else {
        setMessage({
          type: 'success',
          text: result.message || getUploadSuccessMessage('product'),
          showHistoryLink: true,
        })
        setUploadedProducts([])
        setFile(null)
        fetchUploadLogs()
        
        setTimeout(() => {
          setUploadProgress(0)
          setProcessedItems(0)
          setTotalItems(0)
        }, 2000)
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Error uploading products',
      })
      setUploadProgress(0)
      setProcessedItems(0)
      setTotalItems(0)
    } finally {
      setLoading(false)
    }
  }

  const handleViewErrorDetails = async (logId) => {
    try {
      const response = await productService.getUploadLogDetails(logId)
      setErrorDetailsDialog({ open: true, uploadLog: response.data || response })
    } catch (error) {
      console.error('Error fetching upload log details:', error)
    }
  }

  const fetchUploadLogs = useCallback(async ({ silent = false } = {}) => {
    if (logsFetchInFlightRef.current) {
      return
    }
    logsFetchInFlightRef.current = true
    try {
      if (!silent) {
        setLogsLoading(true)
      }
      const response = await productService.getUploadLogs({
        page: logsPage + 1,
        limit: logsRowsPerPage
      })
      
      // Handle response structure: API returns { items: [...], pagination: {...} }
      const logsData = response.items || response.data || response.logs || (Array.isArray(response) ? response : [])
      const totalCount = response.pagination?.total || response.totalCount || response.total || (Array.isArray(logsData) ? logsData.length : 0)
      
      setUploadLogs(Array.isArray(logsData) ? logsData : [])
      setLogsTotalCount(totalCount)
    } catch (error) {
      console.error('Error fetching upload logs:', error)
      console.error('Error details:', error.response?.data || error.message)
      if (!silent) {
        setUploadLogs([])
        setLogsTotalCount(0)
      }
    } finally {
      if (!silent) {
        setLogsLoading(false)
      }
      logsFetchInFlightRef.current = false
    }
  }, [logsPage, logsRowsPerPage])

  const handleLogsPageChange = (event, newPage) => {
    setLogsPage(newPage)
  }

  const handleLogsRowsPerPageChange = (event) => {
    setLogsRowsPerPage(parseInt(event.target.value, 10))
    setLogsPage(0)
  }

  // Download sample CSV
  const handleDownloadSampleCSV = () => {
    const sampleData = [
      ['article_code', 'product_name', 'category', 'division', 'description', 'barcode_1', 'barcode_2', 'barcode_3', 'barcode_4', 'barcode_5', 'brand', 'company_rate', 'tax_category', 'vat', 'dsd', 'unit_of_measure', 'pack_size', 'pack_type', 'tp_map', 'is_purchasable', 'logo_image', 'portrait_image', 'banner_image', 'additional_images', 'source'],
      ['AC-001', 'iPhone 15 Pro', 'Electronics', 'PACKED_COMMODITY', 'Latest Apple smartphone', '1234567890123', '1234567890124', '1234567890125', '', '', 'Apple', '750.00', 'electronics', '15', '1', 'piece', '24', 'carton', 'TP 999 / MAP 1099', 'true', 'https://picsum.photos/200', 'https://picsum.photos/200', 'https://picsum.photos/200', '["https://picsum.photos/200", "https://picsum.photos/200"]', 'shwapno'],
      ['', 'External Showcase Product', 'Electronics', '', 'External item without article code', '2345678901234', '', '', '', '', 'Samsung', '', 'electronics', '', '0', 'piece', '', '', '', '', 'https://picsum.photos/200', '', 'https://picsum.photos/200', '[]', 'external'],
      ['EXT-CUSTOM-01', 'External Product With Code', 'Electronics', '', 'External item with custom article code', '3456789012340', '', '', '', '', 'LG', '', 'electronics', '0', '0', 'piece', '', '', '', '', 'https://picsum.photos/200', '', 'https://picsum.photos/200', '[]', 'external'],
      ['AC-003', 'Sony WH-1000XM5', 'Audio', 'LOOSE_COMMODITY', 'Noise-cancelling headphones', '3456789012345', '3456789012346', '3456789012347', '3456789012348', '3456789012349', 'Sony', '280.00', 'electronics', '7.5', '0', 'piece', '', '', '', 'false', '', 'https://picsum.photos/200', '', '["https://picsum.photos/200"]', 'shwapno'],
    ]

    const csv = sampleData
      .map((row) => row.map((cell, index) => {
        // Handle JSON formatting for additional_images field (last column)
        if (index === row.length - 1 && cell && cell.startsWith('[')) {
          return `"${cell.replace(/"/g, '""')}"`
        }
        return `"${cell}"`
      }).join(','))
      .join('\n')

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', 'sample_products.csv')
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue)
  }

  const handleViewUploadHistory = useCallback(() => {
    setActiveTab(1)
    fetchUploadLogs()
  }, [fetchUploadLogs])

  // Fetch upload logs when tab or pagination changes
  useEffect(() => {
    if (activeTab === 1) {
      fetchUploadLogs()
    }
  }, [activeTab, fetchUploadLogs])

  useUploadHistoryPolling(activeTab === 1, fetchUploadLogs)

  return (
    <>
      <Container maxWidth="lg" sx={{ py: 3 }}>
      <Typography variant="h4" sx={{ mb: 1, fontWeight: 700, color: '#1a1a1a' }}>
        Product Upload
      </Typography>
      <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>
        Import products from CSV file with support for batch processing and image metadata
      </Typography>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="Upload Products" icon={<CloudUploadIcon />} iconPosition="start" />
          <Tab label="Upload History" icon={<HistoryIcon />} iconPosition="start" />
        </Tabs>
      </Box>

      {/* Upload Tab */}
      {activeTab === 0 && (
        <Grid container spacing={3}>
          {/* Upload Section */}
          <Grid item xs={12} md={8}>
            <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
              <CardContent>
                {message && (
                  <UploadImportStatusAlert
                    severity={message.type}
                    onClose={() => setMessage(null)}
                    showHistoryLink={message.showHistoryLink}
                    onViewUploadHistory={handleViewUploadHistory}
                  >
                    {message.text}
                  </UploadImportStatusAlert>
                )}

                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CloudUploadIcon sx={{ color: '#e90000' }} />
                  Upload Products CSV
                </Typography>

                <Box sx={{ mb: 3 }}>
                  <Box
                    sx={{
                      border: isDragOver ? '2px solid #1B5E20' : '2px dashed #e90000',
                      borderRadius: 2,
                      p: 4,
                      textAlign: 'center',
                      cursor: 'pointer',
                      bgcolor: isDragOver ? '#E8F5E9' : '#f8f9fb',
                      transition: 'all 0.3s ease',
                      '&:hover': { 
                        bgcolor: '#E8F5E9',
                        borderColor: '#1B5E20',
                      },
                    }}
                    onClick={() => document.getElementById('file-input').click()}
                    onDragOver={handleDragOver}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <CloudUploadIcon sx={{ fontSize: 56, color: '#e90000', mb: 2 }} />
                    <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                      {file ? file.name : isDragOver ? 'Drop CSV file here' : 'Click to upload or drag and drop'}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      CSV file format (Max 10MB)
                    </Typography>
                    <input
                      id="file-input"
                      type="file"
                      accept=".csv"
                      onChange={handleFileChange}
                      hidden
                    />
                  </Box>
                </Box>

                {/* Batch Size Configuration */}
                <Box sx={{ mb: 3 }}>
                  <TextField
                    label="Batch Size"
                    type="number"
                    value={batchSize}
                    onChange={(e) => setBatchSize(Math.max(1, parseInt(e.target.value, 10) || 10))}
                    fullWidth
                    size="small"
                    helperText="Number of products to process per batch (default: 10)"
                    disabled={loading}
                  />
                </Box>

                {loading && uploadProgress > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
                        Upload Progress
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#666' }}>
                        {uploadProgress}% ({processedItems}/{totalItems} items)
                      </Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={uploadProgress}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        backgroundColor: '#e0e0e0',
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 4,
                          backgroundColor: '#e90000',
                        },
                      }}
                    />
                  </Box>
                )}

                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  onClick={handleUpload}
                  disabled={loading || !file}
                  sx={{
                    bgcolor: '#e90000',
                    py: 1.5,
                    fontWeight: 600,
                    '&:hover': { bgcolor: '#cc0000' },
                  }}
                >
                  {loading ? <CircularProgress size={24} sx={{ mr: 1 }} /> : null}
                  {loading ? 'Uploading...' : 'Upload Products'}
                </Button>

                {uploadedProducts.length > 0 && (
                  <Alert severity="success" sx={{ mt: 3 }}>
                    ✓ Successfully uploaded {uploadedProducts.length} product(s)
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Help & Sample Section */}
          <Grid item xs={12} md={4}>
            {/* Sample Download Card */}
            <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                  <DownloadIcon sx={{ color: '#e90000', fontSize: 40 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Sample CSV
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
                  Download a sample CSV to see the required format.
                </Typography>
                <Button
                  variant="outlined"
                  fullWidth
                  size="large"
                  startIcon={<DownloadIcon sx={{ fontSize: 20 }} />}
                  onClick={handleDownloadSampleCSV}
                  sx={{
                    color: '#e90000',
                    borderColor: '#e90000',
                    fontWeight: 600,
                    '&:hover': { bgcolor: '#E8F5E9' },
                    py: 1.2,
                  }}
                >
                  Download Sample
                </Button>
              </CardContent>
            </Card>

            <InstructionCard {...productUploadInstructions} icon={<InfoIcon />} />
          </Grid>
        </Grid>
      )}

      {/* Uploaded Products Table */}
      {uploadedProducts.length > 0 && activeTab === 0 && (
        <Card sx={{ mt: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Uploaded Products ({uploadedProducts.length})
            </Typography>
            <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8f9fb' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Product Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Article Code</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">
                      Images
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {uploadedProducts.slice(0, 20).map((product, idx) => (
                    <TableRow key={idx} sx={{ '&:hover': { bgcolor: '#f8f9fb' } }}>
                      <TableCell>{product.product_name}</TableCell>
                      <TableCell sx={{ color: '#666' }}>{product.sku}</TableCell>
                      <TableCell>{product.category}</TableCell>
                      <TableCell align="center">
                        {(() => {
                          let imageCount = 0;
                          if (product.image_url?.logo) imageCount++;
                          if (product.image_url?.portrait) imageCount++;
                          if (product.image_url?.banner) imageCount++;
                          if (product.image_url?.additional_images?.length > 0) imageCount += product.image_url.additional_images.length;
                          return imageCount;
                        })()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {uploadedProducts.length > 20 && (
              <Typography variant="caption" sx={{ display: 'block', mt: 2, color: '#999' }}>
                Showing 20 of {uploadedProducts.length} products
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      {/* Upload History Tab */}
      {activeTab === 1 && (
        <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Upload History
              </Typography>
              <UploadHistoryRefreshButton
                onClick={fetchUploadLogs}
                loading={logsLoading}
              />
            </Box>

            <UploadHistoryTableWrapper
              loading={logsLoading}
              isEmpty={uploadLogs.length === 0}
            >
              <>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f8f9fb' }}>
                      <TableCell sx={{ fontWeight: 600 }}>File Name</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="center">
                        Total
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="center">
                        Success
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="center">
                        Failed
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="center">
                        Success Rate
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {uploadLogs.map((log) => (
                      <TableRow key={log.id} sx={{ '&:hover': { bgcolor: '#f8f9fb' } }}>
                        <TableCell>{log.file_name}</TableCell>
                        <TableCell sx={{ color: '#666' }}>
                          {formatDate(log.created_at)}
                        </TableCell>
                        <TableCell align="center">{log.total_rows}</TableCell>
                        <TableCell align="center" sx={{ color: '#4caf50', fontWeight: 600 }}>
                          {log.successful_imports}
                        </TableCell>
                        <TableCell align="center" sx={{ color: '#f44336', fontWeight: 600 }}>
                          {log.failed_imports}
                        </TableCell>
                        <TableCell align="center">
                          {log.total_rows > 0 
                            ? `${Math.round((log.successful_imports / log.total_rows) * 100)}%` 
                            : '0%'
                          }
                        </TableCell>
                        <TableCell>
                          <UploadLogStatusChip status={log.status} />
                        </TableCell>
                        <TableCell align="center">
                          {log.failed_imports > 0 ? (
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<ErrorIcon />}
                              onClick={() => handleViewErrorDetails(log.id)}
                            >
                              View {log.failed_imports} Error{log.failed_imports !== 1 ? 's' : ''}
                            </Button>
                          ) : (
                            <span style={{ color: '#999', fontSize: '0.75rem' }}>No errors</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={logsTotalCount}
                page={logsPage}
                onPageChange={handleLogsPageChange}
                rowsPerPage={logsRowsPerPage}
                onRowsPerPageChange={handleLogsRowsPerPageChange}
                rowsPerPageOptions={[5, 10, 25, 50]}
                showFirstButton
                showLastButton
                sx={{
                  borderTop: 1,
                  borderColor: 'divider',
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                    fontWeight: 500,
                  },
                }}
              />
            </>
            </UploadHistoryTableWrapper>
          </CardContent>
        </Card>
      )}
      </Container>

      {/* Error Details Dialog */}
      <UploadErrorDetailsDialog 
        open={errorDetailsDialog.open}
        onClose={() => setErrorDetailsDialog({ open: false, uploadLog: null })}
        uploadLog={errorDetailsDialog.uploadLog}
        onExportError={(text) => setMessage({ type: 'error', text })}
      />
    </>
  )
}

export default ProductUploadPage

